import { type ReactElement, useLayoutEffect, useId, useState, useMemo, useRef, forwardRef } from 'react';
import classnames from 'classnames';

import { DropBase, type ArrowGuardDistance, type Placement } from '@hh.ru/magritte-internal-drop-base';
import { Action } from '@hh.ru/magritte-ui-action';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { STRETCHED_WRAPPER_CLASS } from '@hh.ru/magritte-ui-button';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { TooltipArrow } from '@hh.ru/magritte-ui-tooltip/TooltipArrow';
import { TooltipProps } from '@hh.ru/magritte-ui-tooltip/types';
import { Text } from '@hh.ru/magritte-ui-typography';
import { BaseText } from '@hh.ru/magritte-ui-typography/BaseText';

import styles from './tooltip.less';

const CSS_CLASSES = {
    enter: styles.tooltipEnter,
    enterActive: styles.tooltipEnterActive,
    exit: styles.tooltipExit,
    exitActive: styles.tooltipExitActive,
};

const PLACEMENTS_LIST: Placement[] = [
    'left-center',
    'top-center',
    'right-center',
    'bottom-center',
    'left-top',
    'left-bottom',
    'top-left',
    'top-right',
    'right-bottom',
    'right-top',
    'bottom-left',
    'bottom-right',
];

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
    (
        {
            visible,
            activatorRef,
            onClose = noop,
            maxWidth = 400,
            title,
            children,
            showClose,
            closeByClickOutside = true,
            placement,
            buttons,
            forcePlacement,
            'aria-label-close': ariaLabelClose,
            role,
            size = 'medium',
            ignoreIntersections = true,
            lockPlacement = true,
            guardDistanceToActivator: _guardDistanceToActivator,
            ...props
        }: TooltipProps,
        ref
    ): ReactElement | null => {
        const { isMobile } = useBreakpoint();
        const id = useId();
        const ariaLabledBy = `tooltip-aria-label-${id}`;
        const ariaDescribedBy = `tooltip-aria-description-${id}`;
        const [animationTimeout, setAnimationTimeout] = useState(200);
        const [arrowGuardDistance, setArrowGuardDistance] = useState<ArrowGuardDistance>({
            left: { top: 0, bottom: 0, center: 0 },
            right: { top: 0, bottom: 0, center: 0 },
            top: { left: 0, center: 0, right: 0 },
            bottom: { left: 0, center: 0, right: 0 },
        });
        const placementsList = useMemo(() => {
            const placementsArray: Placement[] = Array.isArray(placement) ? placement : [placement];
            return forcePlacement
                ? placementsArray
                : [...placementsArray, ...PLACEMENTS_LIST.filter((place) => !placement.includes(place))];
        }, [placement, forcePlacement]);
        const tooltipElementRef = useRef<HTMLDivElement>(null);

        const ariaRole = role || (buttons ? 'alertdialog' : 'tooltip');
        const isSmall = size === 'small';
        const guardDistanceToActivator = _guardDistanceToActivator ?? (isSmall ? 11 : 20);

        useLayoutEffect(() => {
            if (!tooltipElementRef.current) {
                return;
            }

            const style = window.getComputedStyle(tooltipElementRef.current);
            const animationTimeout = parseInt(style.getPropertyValue('--magritte-tooltip-animation-duration'), 10);
            if (Number.isInteger(animationTimeout)) {
                setAnimationTimeout(animationTimeout);
            }

            const borderRadius = parseInt(style.getPropertyValue('--magritte-tooltip-border-radius'), 10);
            const arrowWidth = parseInt(style.getPropertyValue('--magritte-tooltip-arrow-width'), 10);
            if (Number.isInteger(borderRadius) && Number.isInteger(arrowWidth)) {
                const arrowGap = borderRadius + arrowWidth / 2;
                setArrowGuardDistance({
                    left: {
                        top: arrowGap,
                        bottom: arrowGap,
                        center: 0,
                    },
                    right: {
                        top: arrowGap,
                        bottom: arrowGap,
                        center: 0,
                    },
                    top: {
                        left: arrowGap,
                        center: 0,
                        right: arrowGap,
                    },
                    bottom: {
                        left: arrowGap,
                        center: 0,
                        right: arrowGap,
                    },
                });
            }
        }, [buttons, setAnimationTimeout, isSmall, visible]);

        if (isMobile) {
            return null;
        }

        return (
            <Layer layer="tooltip">
                <DropBase
                    {...props}
                    placement={placementsList}
                    visible={visible}
                    activatorRef={activatorRef}
                    arrowGuardDistance={arrowGuardDistance}
                    closeByClickOutside={closeByClickOutside}
                    onClose={onClose}
                    autoFocusWhenOpened={ariaRole !== 'tooltip'}
                    guardDistanceToActivator={guardDistanceToActivator}
                    guardDistanceToViewport={0}
                    allowShrinkHeightToFitIntoViewport={false}
                    animationClassNames={CSS_CLASSES}
                    animationTimeout={animationTimeout}
                    ignoreIntersections={ignoreIntersections}
                    role={ariaRole}
                    aria-labelledby={title && role !== 'tooltip' ? ariaLabledBy : undefined}
                    aria-describedby={role !== 'tooltip' ? ariaDescribedBy : undefined}
                    ref={ref}
                    lockPlacement={lockPlacement}
                >
                    {() => (
                        <div
                            data-qa="tooltip"
                            ref={tooltipElementRef}
                            className={classnames(styles.tooltip, {
                                [styles.tooltipWithButtons]: !isSmall && !!buttons,
                                [styles.tooltipWithClose]: !isSmall && showClose,
                                [styles.tooltipSmall]: isSmall,
                            })}
                            style={{
                                maxWidth: `${maxWidth}px`,
                            }}
                        >
                            {title && !isSmall && (
                                <div className={styles.tooltipTitle}>
                                    <BaseText
                                        id={ariaLabledBy}
                                        data-qa="tooltip-title"
                                        Element="div"
                                        style="primary"
                                        typography="title-5-semibold"
                                    >
                                        {title}
                                    </BaseText>
                                </div>
                            )}
                            <Text
                                typography={isSmall ? 'paragraph-3-regular' : 'paragraph-2-regular'}
                                id={ariaDescribedBy}
                            >
                                {children}
                            </Text>
                            {buttons && !isSmall && (
                                <div className={classnames(styles.tooltipButtons, STRETCHED_WRAPPER_CLASS)}>
                                    {typeof buttons === 'function' ? buttons(onClose) : buttons}
                                </div>
                            )}
                            {showClose && !isSmall && (
                                <div className={styles.tooltipClose}>
                                    <Action
                                        data-qa="tooltip-close-action"
                                        aria-label={ariaLabelClose}
                                        icon={CrossOutlinedSize24}
                                        mode="secondary"
                                        onClick={() => onClose?.()}
                                        style="contrast"
                                    />
                                </div>
                            )}
                            <TooltipArrow className={styles.tooltipArrow} size={size} />
                        </div>
                    )}
                </DropBase>
            </Layer>
        );
    }
);

Tooltip.displayName = 'Tooltip';
